export type EventOwner = "backend" | "posthog";

type EventPropertyValue = boolean | number | string | null | undefined;

type ReviewerRole = "CHECKER" | "MAKER" | undefined;

export type EmptyStateSection =
  | "capital_activity"
  | "documents"
  | "investments"
  | "offerings"
  | "reporting";

export type EmptyStateReason = "genuinely_empty" | "no_permission_suspected";

export type CoInvestIoiOutcome = "attempted" | "succeeded";

export type EventPropertyMap = {
  gp_review_task_viewed: {
    task_type: string | undefined;
    task_status: string | undefined;
    reviewer_role: ReviewerRole;
    workflow_task_id: string | undefined;
    subscription_id: string | undefined;
  };
  gp_review_task_reviewed: {
    action: string;
    task_type: string | undefined;
    reviewer_role: ReviewerRole;
    workflow_task_id: string | undefined;
    subscription_id: string | undefined;
    has_comment: boolean;
  };
  gp_review_task_review_attempted: {
    action: string;
    task_type: string | undefined;
    reviewer_role: ReviewerRole;
    workflow_task_id: string | undefined;
    subscription_id: string | undefined;
  };
  gp_review_task_review_failed: {
    action: string;
    task_type: string | undefined;
    reviewer_role: ReviewerRole;
    workflow_task_id: string | undefined;
    subscription_id: string | undefined;
    status_code: number | undefined;
  };
  api_error: {
    status_code: number | undefined;
    endpoint: string | undefined;
    method: string | undefined;
    trace_id: string | undefined;
  };
  empty_state_rendered: {
    section: EmptyStateSection;
    reason: EmptyStateReason | undefined;
  };
  onboarding_invite_create_attempted: {
    portal_type: string | undefined;
    invite_type: string;
    role_count: number;
  };
  onboarding_invite_create_failed: {
    portal_type: string | undefined;
    invite_type: string;
    role_count: number;
    status_code: number | undefined;
  };
  onboarding_registration_started: {
    portal_type: string | undefined;
    invited_user_id: string | undefined;
  };
  onboarding_registration_completed: {
    portal_type: string | undefined;
    invited_user_id: string | undefined;
    country_code: string | undefined;
  };
  onboarding_email_activation_viewed: {
    portal_type: string | undefined;
  };
  onboarding_first_login_succeeded: {
    portal_type: string | undefined;
    user_id: string;
  };
  onboarding_login_failed: {
    portal_type: string | undefined;
    status_code: number | undefined;
  };
  coinvest_teaser_viewed: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_nda_opened: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_nda_dismissed: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_nda_downloaded: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_data_room_accessed: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_nda_sign_attempted: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_reject_modal_opened: {
    deal_opportunity_id: string;
  };
  coinvest_deal_reject_attempted: {
    deal_opportunity_id: string;
  };
  coinvest_ioi_submitted: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
    outcome: CoInvestIoiOutcome;
  };
  coinvest_commitment_attempted: {
    deal_opportunity_id: string;
  };
  coinvest_deal_opportunity_viewed: {
    deal_opportunity_id: string;
    deal_id: string | undefined;
  };
  coinvest_deal_tab_viewed: {
    deal_opportunity_id: string;
    tab: string;
  };
  coinvest_document_downloaded: {
    deal_opportunity_id: string;
    document_id: string;
  };
  coinvest_question_asked: {
    deal_opportunity_id: string;
  };
  coinvest_deals_filtered: {
    filter: string;
  };
};

export type AnalyticsEventName = keyof EventPropertyMap;

export type EventProperties<TEvent extends AnalyticsEventName> =
  EventPropertyMap[TEvent];

export const EVENT_OWNER: Record<AnalyticsEventName, EventOwner> = {
  gp_review_task_viewed: "posthog",
  gp_review_task_reviewed: "posthog",
  gp_review_task_review_attempted: "posthog",
  gp_review_task_review_failed: "posthog",
  api_error: "posthog",
  empty_state_rendered: "posthog",
  onboarding_invite_create_attempted: "posthog",
  onboarding_invite_create_failed: "posthog",
  onboarding_registration_started: "posthog",
  onboarding_registration_completed: "posthog",
  onboarding_email_activation_viewed: "posthog",
  onboarding_first_login_succeeded: "posthog",
  onboarding_login_failed: "posthog",
  coinvest_teaser_viewed: "posthog",
  coinvest_nda_opened: "posthog",
  coinvest_nda_dismissed: "posthog",
  coinvest_nda_downloaded: "posthog",
  coinvest_data_room_accessed: "posthog",
  coinvest_nda_sign_attempted: "posthog",
  coinvest_reject_modal_opened: "posthog",
  coinvest_deal_reject_attempted: "posthog",
  coinvest_ioi_submitted: "posthog",
  coinvest_commitment_attempted: "posthog",
  coinvest_deal_opportunity_viewed: "posthog",
  coinvest_deal_tab_viewed: "posthog",
  coinvest_document_downloaded: "posthog",
  coinvest_question_asked: "posthog",
  coinvest_deals_filtered: "posthog",
};

export type ReservedEventProperties = {
  event_id: string;
  schema_version: number;
};

export const ANALYTICS_SCHEMA_VERSION = 1;

export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;

const _flatPropertiesCheck: Record<
  AnalyticsEventName,
  Record<string, EventPropertyValue>
> = {} as EventPropertyMap;
void _flatPropertiesCheck;
